import { useDialog } from "helper/customHook";
import ViewTable from "modules/DeclareLeave/components/CustomTable";
import { listRequestLeaveData } from "modules/DeclareLeave/redux/selector";
import { useDispatch, useSelector } from "react-redux";
import { Box, Tooltip, Typography } from "@mui/material";
import pxToRem from "theme/base/pxToRem";
import {
  ApproveStatus,
  ApproveStatusValue,
  statusColor,
} from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/const";

import { DIALOG } from "helper/enum/dialogEnum";
import LeaveRequestDetails from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/components/Modals/LeaveRequestDetail";
import { PopoverAction } from "./PopoverAction";
import EmptyState from "components/common/EmptyState";
import { setDeleteRequestData } from "modules/DeclareLeave/redux/deleteRequest";
import {
  getRequestLeaveDetailStart,
  setRequestLeaveDetailId,
} from "modules/DeclareLeave/redux/getRequestLeaveDetail";
import { formatDateStringToDDMMYYYY } from "helper/utils";
import { SCREEN } from "helper/roleChecker/Screens";
import { getListRequestLeaveConfigStart } from "modules/DeclareLeave/redux/getListRequestLeaveConfig";
import { isNumber } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import pathRoutes from "routes/pathRoutes";
import { declareLeaveURL } from "modules/DeclareLeave/Pages/Main/DeclareLeaveContent/index";
import { memo } from "react";
import { setCancelRequestData } from "modules/DeclareLeave/redux/cancelRequest";
import { ConfirmModalDelete } from "./ConfirmModal";

export const ListLeaveRequest = memo(() => {
  const { id } = useParams();

  const dispatch = useDispatch();
  const data = useSelector(listRequestLeaveData);
  const requestLeaveDetailDialog = useDialog(DIALOG.VIEW_REQUEST_LEAVE_DETAIL);
  const approveDialog = useDialog(DIALOG.MODAL_CONFIRM);
  const requestLeaveDialog = useDialog(DIALOG.REQUEST_LEAVE);
  const navigate = useNavigate();

  const renderColumnContent = (value: string, separator: string = "#") => {
    const items = (value ?? "").split(separator) ?? [];
    return (
      <Box display={"flex"} flexDirection={"column"}>
        {items.map((item, index) => (
          <Typography
            key={index}
            sx={(theme) => ({
              fontSize: pxToRem(12),
              color: theme.palette.text.textPrimary,
            })}
          >
            {item}
          </Typography>
        ))}
      </Box>
    );
  };

  const columns = [
    {
      id: "index",
      accessorKey: "index",
      header: "STT",
      width: 50,
      footer: (props) => props.column.id,
      cell: (info) => (
        <Typography
          sx={(theme) => ({
            fontSize: pxToRem(12),
            color: theme.palette.text.textPrimary,
            width: "100%",

            textAlign: "center",
          })}
        >
          {info.row.index + 1 < 10
            ? `0${info.row.index + 1}`
            : info.row.index + 1}
        </Typography>
      ),
      pinPosition: "left",
    },
    {
      id: "registerDate",
      accessorKey: "registerDate",
      header: "Ngày tạo đơn",
      footer: (props) => props.column.id,
      cell: (info) => (
        <Typography
          sx={(theme) => ({
            fontSize: pxToRem(12),
            color: theme.palette.text.textPrimary,
            width: "100%",
            textAlign: "center",
          })}
        >
          {formatDateStringToDDMMYYYY(info.getValue())}
        </Typography>
      ),
      minSize: 130,
      pinPosition: "center",
    },
    {
      id: "type",
      accessorKey: "type",
      header: "Loại nghỉ",
      cell: (info) => renderColumnContent(info.getValue()),
      footer: (props) => props.column.id,
      minSize: 120,
      enablePinning: true,
    },
    {
      accessorKey: "dayOffs",
      header: () => "Ngày nghỉ",
      footer: (props) => props.column.id,
      cell: (info) => renderColumnContent(info.getValue()),
      minSize: 250,
    },
    {
      accessorKey: "totalLeaveDay",
      header: () => <span>Số ngày nghỉ</span>,
      footer: (props) => props.column.id,
      cell: (info) => (
        <Typography
          sx={(theme) => ({
            fontSize: pxToRem(12),
            color: theme.palette.text.textPrimary,
            width: "100%",
            textAlign: "center",
          })}
        >
          {isNumber(info.getValue()) &&
          info.getValue() < 10 &&
          info.getValue() >= 1
            ? `0${info.getValue()}`
            : info.getValue()}
        </Typography>
      ),
      minSize: 120,
    },
    {
      accessorKey: "leaveReason",
      header: "Lý do nghỉ",
      cell: (info) => (
        <Tooltip title={info?.getValue()}>
          <Typography
            sx={{
              fontSize: pxToRem(12),
              overflow: "hidden",
              textOverflow: "ellipsis",
              // height: pxToRem(60),
              textAlign: "left",
            }}
          >
            {info?.getValue()?.length > 60
              ? `${info?.getValue()?.slice(0, 60)}...`
              : info?.getValue()}
          </Typography>
        </Tooltip>
      ),
      footer: (props) => props.column.id,
      minSize: 180,
    },
    {
      accessorKey: "relationPersonName",
      header: "Người liên quan",
      footer: (props) => props.column.id,
      minSize: 150,
    },
    {
      accessorKey: "approveName",
      header: "Người duyệt",
      footer: (props) => props.column.id,
      cell: (info) => renderColumnContent(info.getValue(), ","),
      minSize: 150,
    },
    {
      id: "modifiedDate",
      accessorKey: "modifiedDate",
      header: "Ngày cập nhật đơn",
      cell: (info) => (
        <Typography
          sx={(theme) => ({
            fontSize: pxToRem(12),
            color: theme.palette.text.textPrimary,
            width: "100%",
            textAlign: "center",
          })}
        >
          {info.getValue() ? formatDateStringToDDMMYYYY(info.getValue()) : ""}
        </Typography>
      ),
      footer: (props) => props.column.id,
      minSize: 155,
      pinPosition: "left",
    },
    {
      accessorKey: "status",
      header: "Trạng thái",
      footer: (props) => props.column.id,
      cell: (info) => {
        const status = info.getValue();
        return (
          <Typography
            sx={(theme) => ({
              fontSize: pxToRem(12),
              fontWeight: 600,
              color: statusColor(theme, status, "color"),
              textAlign: "center",
              width: "100%",
            })}
          >
            {ApproveStatus[status]}
          </Typography>
        );
      },
      minSize: 100,
    },
    {
      accessorKey: "action",
      header: "",
      cell: (info) => (
        <PopoverAction
          showAction={[
            ApproveStatusValue.PENDING,
            ApproveStatusValue.APPROVE,
            ApproveStatusValue.AUTO_APPROVE,
          ].includes(info.row.original.status)}
          editable={info.row.original.editable}
          isDeletable={info.row.original.delete}
          isCancelable={info.row.original.cancel}
          editAction={(e) => {
            e.stopPropagation();
            const month = (info.row.original.dayOffs ?? "")
              .split("#")[0]
              .split("/")[1];
            const year = info.row.original.dayOffs
              ?.split("đến ")[0]
              ?.split("/")
              ?.pop();

            dispatch(
              getListRequestLeaveConfigStart({
                screenCode: SCREEN.IP_016,
                month,
                year,
              })
            );
            dispatch(setRequestLeaveDetailId(info.row.original.id));
            dispatch(getRequestLeaveDetailStart());
            requestLeaveDialog.open();
          }}
          deleteAction={(e) => {
            e.stopPropagation();
            dispatch(setDeleteRequestData(info.row.original.id));
            approveDialog.open();
          }}
          cancelAction={(e) => {
            e.stopPropagation();
            dispatch(setCancelRequestData(info.row.original.id));
            approveDialog.open();
          }}
        />
      ),
      minSize: 70,
    },
  ];

  const handleViewDetail = (idRecord: string) => {
    if (idRecord === id) {
      requestLeaveDetailDialog.open();
    } else {
      navigate(
        `${pathRoutes.declareLeave.base}/${declareLeaveURL.orderLeave}/${declareLeaveURL.leave}/${idRecord}`,
        {
          replace: true,
        }
      );
    }
  };

  return (
    <>
      <ViewTable
        data={data}
        columns={columns}
        columnPinning={{
          left: [],
          right: ["action"],
        }}
        handleFetchMore={() => {}}
        messageEmpty={
          <EmptyState
            message="Chưa có đơn xin nghỉ nào"
            sx={(theme) => ({
              h6: {
                color: theme.palette.text.textSecondary,
              },
            })}
          />
        }
        handleOnClickedRow={(item) => {
          handleViewDetail(item.id);
        }}
        height={"auto"}
      />
      <LeaveRequestDetails />
      <ConfirmModalDelete />
    </>
  );
});
